import { useEffect, useState } from 'react';
import { useSettings } from '@/api/use-settings';
import { CalendarImportButton } from '@/components/CalendarImportButton';
import { CalendarSidePanel } from '@/components/CalendarSidePanel';
import { ALL_KINDS, KindTabs } from '@/components/KindTabs';
import { TaskCalendar } from '@/components/TaskCalendar';
import { TaskDetail } from '@/components/TaskDetail';
import { TaskTable } from '@/components/TaskTable';
import {
  buildCalendarCells,
  formatRangeLabel,
  shiftByUnit,
  today,
  weekNumber,
  type CalendarUnit,
} from '@/tasks/calendar-range';
import { moveTask, toDraftTask, type DraftRange } from '@/tasks/time-grid';
import { listTitleOptionsByKind } from '@/tasks/task-title';
import { fmtManualOf, useFmtItemsByKind } from '@/api/use-monthly-fmt';
import {
  ALL_ASSIGNEES,
  filterByAssignee,
  filterByDateRange,
  filterByTeam,
  listAssigneeOptions,
  type Task,
  type TeamId,
} from '@/tasks/task';

/**
 * 表示するビュー。
 */
export type ViewMode = 'table' | 'calendar';

/**
 * テーブルで見る期間。
 *
 * 月単位が既定だが、過去分をまとめて探したいときに全期間へ切り替える。
 */
export type TableRange = 'month' | 'all';

/** @graph-connects none */
const VIEW_LABEL: Record<ViewMode, string> = {
  table: 'テーブル',
  calendar: 'カレンダー',
};

/** @graph-connects none */
const UNIT_LABEL: Record<CalendarUnit, string> = {
  month: '月',
  week: '週',
  day: '日',
};

/** @graph-connects none */
const SEGMENT_ON = 'bg-[var(--color-accent)] px-3 py-1 text-sm text-[var(--color-bg-surface)]';
/** @graph-connects none */
const SEGMENT_OFF =
  'bg-[var(--color-bg-surface)] px-3 py-1 text-sm text-[var(--color-text-secondary)]';

/**
 * TaskManagement の props。
 */
export interface TaskManagementProps {
  /** 表示するタスク。アプリ全体で 1 つの一覧を持ち、ここへ渡す */
  tasks: readonly Task[];
  /** タスクを直したときに呼ばれる。保存が終わるまで待てるよう Promise を返す */
  onUpdateTask: (task: Task) => Promise<boolean>;
  /** カレンダーを引いてタスクを作ったときに呼ばれる */
  onCreateTasks: (created: readonly Omit<Task, 'id'>[]) => Promise<boolean>;
  /** 初期表示の基準日（`YYYY-MM-DD`）。既定は今日 */
  initialDate?: string;
  /** 初期表示のビュー */
  initialView?: ViewMode;
  /** カレンダーの初期表示単位 */
  initialUnit?: CalendarUnit;
  /** 表示するチーム。サイドバーで選んだものが渡る */
  team: TeamId;
  /** タスクを読み直す。カレンダーから取り込んだあとに呼ぶ */
  onReloadTasks?: () => void;
  /** タスクを消す。渡さなければ詳細に削除を出さない */
  onDeleteTask?: (id: string) => Promise<boolean>;
}

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスク管理の画面。タスクをテーブルと、15 分刻みの時間軸カレンダー（月/週/日）で表示する
 * @graph-connects TaskTable [calls] タスクを表で描画する
 * @graph-connects TaskCalendar [calls] タスクをカレンダーで描画する
 * @graph-connects filterByTeam [calls] 表示チームのタスクに絞る
 * @graph-connects moveTask [calls] 動かした先に合わせてタスクの日時を決める
 * @graph-connects today [calls] 今日の日付を既定の表示位置に使う
 * @graph-connects listTitleOptionsByKind [calls] 種別ごとの区分の選択肢を集める
 * @graph-connects useFmtItemsByKind [calls] 区分の選択肢の元になる FMT を読む
 * @graph-connects fmtManualOf [calls] 元の FMT 項目のマニュアルを引く
 */
export function TaskManagement({
  team,
  tasks: allTasks,
  onUpdateTask,
  onCreateTasks,
  // 開いたらまず今週が出るようにする。日付を指定するのはテストと、画面から呼ぶときだけ
  initialDate = today(),
  // 既定はカレンダー。まず「いつ何があるか」を見る使い方が中心なので、表より先に出す
  initialView = 'calendar',
  // カレンダーは週表示を既定にする（当面の予定を把握するのが主な用途）
  initialUnit = 'week',
  onReloadTasks,
  onDeleteTask,
}: TaskManagementProps) {
  const [baseDate, setBaseDate] = useState(initialDate);
  // 担当者の選択肢はメンバー設定を元にする（タスクがまだ無い人も選べるように）
  const { settings: members } = useSettings('members');
  // 種別の選択肢には、種別設定に登録したもの（まだタスクが無い種別）も入れる
  const { settings: kindSettings } = useSettings('kind-settings');
  const [view, setView] = useState<ViewMode>(initialView);
  const [unit, setUnit] = useState<CalendarUnit>(initialUnit);
  const [kind, setKind] = useState(ALL_KINDS);
  const [selected, setSelected] = useState<Task | null>(null);
  // カレンダーを引いて作った、まだ保存していない予定
  const [creating, setCreating] = useState<Omit<Task, 'id'> | null>(null);
  const [assignee, setAssignee] = useState(ALL_ASSIGNEES);
  const [tableRange, setTableRange] = useState<TableRange>('month');
  // 区分の選択肢の元（月次経理・決算タスクFMT の全項目）
  const fmtItemsByKind = useFmtItemsByKind();
  // カレンダーの左の一覧でチェックを外した種別・担当者。Google カレンダーのカレンダー表示の切り替えと同じ
  const [hiddenKinds, setHiddenKinds] = useState<ReadonlySet<string>>(new Set());
  const [hiddenAssignees, setHiddenAssignees] = useState<ReadonlySet<string>>(new Set());

  // チームが変わると種別も担当者の顔ぶれも変わるので、絞り込みは持ち越さない
  useEffect(() => {
    setKind(ALL_KINDS);
    setAssignee(ALL_ASSIGNEES);
    setSelected(null);
  }, [team]);

  // テーブルは常に月単位。カレンダーは選んだ単位の範囲
  const rangeUnit: CalendarUnit = view === 'table' ? 'month' : unit;
  const cells = buildCalendarCells(baseDate, rangeUnit);
  const rangeStart = cells[0]?.date ?? baseDate;
  const rangeEnd = cells[cells.length - 1]?.date ?? baseDate;

  const teamTasks = filterByTeam(allTasks, team);
  // 担当者はそのチームのメンバー設定とタスクに出てくる人だけを並べる（他チームの人を選べても意味がない）
  const assigneeOptions = listAssigneeOptions(
    teamTasks,
    members.filter((member) => member.team === team),
  );
  // 種別も同じく、そのチームの種別設定とタスクで使われているものを入力候補にする
  const kindOptions = [
    ...new Set([
      ...kindSettings.filter((setting) => setting.team === team).map((setting) => setting.name),
      ...teamTasks.map((task) => task.kind),
    ]),
  ].sort((a, b) => a.localeCompare(b, 'ja'));
  // 種別ごとの区分（大分類・事業・中分類）の選択肢。FMT から起こしたタスクが持つ区分を、同じ種別のタスクで選べるようにする
  const titleOptionsByKind = listTitleOptionsByKind(teamTasks, fmtItemsByKind);
  // テーブルで全期間を選んでいるときは期日で絞らない
  const showAll = view === 'table' && tableRange === 'all';
  const periodTasks = showAll ? teamTasks : filterByDateRange(teamTasks, rangeStart, rangeEnd);
  const rangeTasks = filterByAssignee(periodTasks, assignee);
  // 種別の絞り込みはテーブルだけに効かせる（カレンダーは全体の埋まり具合を見るため）
  const visibleTasks =
    view === 'table'
      ? kind === ALL_KINDS
        ? rangeTasks
        : rangeTasks.filter((task) => task.kind === kind)
      : rangeTasks.filter(
          (task) => !hiddenKinds.has(task.kind) && !hiddenAssignees.has(task.assignee),
        );

  /** 一覧のチェックを切り替える。入っていれば外し、無ければ足す */
  const toggleIn = (current: ReadonlySet<string>, key: string): ReadonlySet<string> => {
    const next = new Set(current);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    return next;
  };

  /** 引いて作ったときと同じく、詳細を開いて新しい予定を作る */
  const startCreate = (range: DraftRange) => {
    setCreating(
      toDraftTask(range, {
        team,
        kind: kind === ALL_KINDS ? 'その他タスク' : kind,
        assignee: assignee === ALL_ASSIGNEES ? '' : assignee,
      }),
    );
  };

  return (
    <main className="flex min-h-0 min-w-0 flex-1 flex-col px-8 py-5">
      {/* 画面名は操作のバーの左端に置く（Google カレンダーの「カレンダー」と同じ位置）。
          見出しだけの段を設けると、カレンダーの上に空きができるため */}
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h1 className="m-0 mr-2 text-lg font-semibold tracking-tight text-[var(--color-text-primary)]">
            ADO TASK
          </h1>
          {showAll ? null : (
            <>
              {/* Google カレンダーと同じく、「今日」・前後・見出しの順に並べる */}
              <button
                type="button"
                onClick={() => {
                  setBaseDate(today());
                }}
                className="h-7 rounded-full border border-[#747775] bg-[var(--color-bg-surface)] px-3.5 text-sm font-medium text-[#3c4043] hover:bg-[var(--color-bg-subtle)]"
              >
                今日
              </button>
              <button
                type="button"
                aria-label="前へ"
                onClick={() => {
                  setBaseDate(shiftByUnit(baseDate, rangeUnit, -1));
                }}
                className="h-7 w-7 rounded-full text-lg leading-none text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
              >
                &lsaquo;
              </button>
              <button
                type="button"
                aria-label="次へ"
                onClick={() => {
                  setBaseDate(shiftByUnit(baseDate, rangeUnit, 1));
                }}
                className="h-7 w-7 rounded-full text-lg leading-none text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]"
              >
                &rsaquo;
              </button>
              <span className="text-base font-medium tabular-nums text-[var(--color-text-primary)]">
                {formatRangeLabel(baseDate, rangeUnit)}
              </span>
              {view === 'calendar' && unit === 'week' ? (
                // Google カレンダーと同じく、週表示では週番号を添える
                <span className="rounded bg-[var(--color-bg-subtle)] px-1.5 py-0.5 text-xs text-[var(--color-cal-label)]">
                  第 {weekNumber(baseDate)} 週
                </span>
              ) : null}
            </>
          )}
          <span className="text-sm text-[var(--color-text-muted)]">{visibleTasks.length}件</span>
        </div>

        <div className="flex items-center gap-2">
          {/* 担当者の絞り込み。チームの切り替えはサイドバーにある */}
          <label htmlFor="assignee-filter" className="sr-only">
            担当で絞り込む
          </label>
          <select
            id="assignee-filter"
            value={assignee}
            onChange={(event) => {
              setAssignee(event.target.value);
              setSelected(null);
            }}
            className={
              assignee === ALL_ASSIGNEES
                ? 'rounded-full border border-[#747775] bg-[var(--color-bg-surface)] px-3 py-1 text-sm text-[#3c4043]'
                : 'rounded-full border border-[var(--color-doing-border)] bg-[var(--color-doing-bg)] px-3 py-1 text-sm text-[var(--color-doing-text)]'
            }
          >
            <option value={ALL_ASSIGNEES}>全員</option>
            {assigneeOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {view === 'calendar' ? (
            // 月/週/日 は Google カレンダーと同じく右上に置く
            <div
              role="tablist"
              aria-label="カレンダーの単位"
              className="inline-flex overflow-hidden rounded-full border border-[#747775]"
            >
              {(['month', 'week', 'day'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={unit === mode}
                  onClick={() => {
                    setUnit(mode);
                  }}
                  className={
                    unit === mode
                      ? 'bg-[#c2e7ff] px-3 py-1 text-sm font-medium text-[#001d35]'
                      : 'bg-[var(--color-bg-surface)] px-3 py-1 text-sm text-[#3c4043] hover:bg-[var(--color-bg-subtle)]'
                  }
                >
                  {UNIT_LABEL[mode]}
                </button>
              ))}
            </div>
          ) : null}
          <div
            role="tablist"
            aria-label="表示の切り替え"
            className="inline-flex overflow-hidden rounded border border-[var(--color-border-subtle)]"
          >
            {(['table', 'calendar'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="tab"
                aria-selected={view === mode}
                onClick={() => {
                  setView(mode);
                }}
                className={view === mode ? SEGMENT_ON : SEGMENT_OFF}
              >
                {VIEW_LABEL[mode]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view === 'table' ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* 種別タブは表の上に置く。表の見出しと続けて読めるほうが絞り込み結果が分かりやすい */}
          <KindTabs tasks={rangeTasks} selected={kind} onSelect={setKind} />
          <div className="min-h-0 flex-1 overflow-y-auto">
            <TaskTable tasks={visibleTasks} onSelect={setSelected} />
          </div>
          {/* 月別／全期間はカレンダーの 月/週/日 と同じ右下に置く（同じ役割の操作なので位置も揃える） */}
          <div className="mt-2 flex shrink-0 justify-end">
            <div
              role="tablist"
              aria-label="期間の切り替え"
              className="inline-flex overflow-hidden rounded border border-[var(--color-border-subtle)]"
            >
              {(['month', 'all'] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={tableRange === mode}
                  onClick={() => {
                    setTableRange(mode);
                  }}
                  className={tableRange === mode ? SEGMENT_ON : SEGMENT_OFF}
                >
                  {mode === 'month' ? '月別' : '全期間'}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 gap-2">
          {/* 左の操作パネル。Google カレンダーと同じく、作成・小さな月カレンダー・時間の分析・カレンダーの一覧を置く */}
          <CalendarSidePanel
            baseDate={baseDate}
            onPickDate={setBaseDate}
            onCreate={() => {
              startCreate({ date: baseDate, startMinutes: 10 * 60, endMinutes: 11 * 60 });
            }}
            rangeCells={cells}
            rangeTasks={visibleTasks}
            kinds={kindOptions}
            hiddenKinds={hiddenKinds}
            onToggleKind={(key) => {
              setHiddenKinds(toggleIn(hiddenKinds, key));
            }}
            assignees={assigneeOptions}
            hiddenAssignees={hiddenAssignees}
            onToggleAssignee={(key) => {
              setHiddenAssignees(toggleIn(hiddenAssignees, key));
            }}
          />
          <div className="flex min-h-0 min-w-0 flex-1 flex-col">
            <TaskCalendar
              baseDate={baseDate}
              unit={unit}
              tasks={visibleTasks}
              onSelect={setSelected}
              onMove={(task, target) => {
                // 失敗したときの理由は画面上部の帯が出す
                void onUpdateTask(moveTask(task, target));
              }}
              onResize={(task) => {
                void onUpdateTask(task);
              }}
              // 引き終えたら詳細を開く。内容が空のまま保存すると一覧で見分けが付かない
              onCreate={startCreate}
            />
          </div>
        </div>
      )}

      {creating === null ? null : (
        <TaskDetail
          task={{ ...creating, id: '' }}
          assignees={assigneeOptions}
          kinds={kindOptions}
          titleOptionsByKind={titleOptionsByKind}
          onSave={(saved) => {
            // 内容が空のままでは一覧で見分けが付かないので、入れずに閉じたら作らない
            if (saved.title.trim() !== '') {
              // 作る前なので ID は無い。保存のときに Firestore が採番する
              const { id: _unused, ...created } = saved;
              void onCreateTasks([created]);
            }
            setCreating(null);
          }}
          onClose={() => {
            setCreating(null);
          }}
        />
      )}

      {selected === null ? null : (
        <TaskDetail
          task={selected}
          fmtManual={fmtManualOf(selected, fmtItemsByKind)}
          onDelete={
            onDeleteTask === undefined
              ? undefined
              : () => {
                  // 失敗したときの理由は画面上部の帯が出す
                  void onDeleteTask(selected.id);
                  setSelected(null);
                }
          }
          assignees={assigneeOptions}
          kinds={kindOptions}
          titleOptionsByKind={titleOptionsByKind}
          onSave={(saved) => {
            // 失敗したときの表示は画面上部の帯が担うので、ここでは投げっぱなしにする
            void onUpdateTask(saved);
            setSelected(null);
          }}
          onClose={() => {
            setSelected(null);
          }}
        />
      )}

      {/* カレンダー取り込みは画面の最下部・左端。普段の操作ではないので、一覧を
          読み終えた位置に置く（上部の操作列を混雑させない） */}
      <div className="mt-3 flex shrink-0 flex-wrap items-center justify-between gap-4">
        <CalendarImportButton
          team={team}
          baseDate={today()}
          assignees={assigneeOptions}
          defaultAssignee={assignee === ALL_ASSIGNEES ? '' : assignee}
          onImported={onReloadTasks}
        />
      </div>
    </main>
  );
}
